<template>
  <div v-bind:class="[large ? 'vue-wrapper' : 'skeleton-container', dashboarditem.loading || dashboarditem.updating ? 'active' : '']">
    <TheLoader v-if="large" :small="false" :active="dashboarditem.loading || dashboarditem.updating" :update="dashboarditem.updating"/>

    <table v-else class="clickable oddeven sortable skeleton-content">
      <thead>
        <tr>
          <th v-for="(thClass, thIndex) in skeletonThLines" v-bind:key="'sk-' + thIndex" v-bind:class="[thClass]"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(trLine, trIndex) in skeletonTdLines" v-bind:key="'sk-tr-' + trIndex">
          <td v-for="(tdClass, tdIndex) in trLine" v-bind:key="'sk-td-' + trIndex + '-' + tdIndex " v-bind:class="[tdClass]"></td>
        </tr>
      </tbody>
    </table>

    <section class="table-card-container">
      <table class="visible oddeven sortable" :class="{'fixed': large}">
        <thead class="border">
          <tr>
            <th v-for="(heading, index) in dashboarditem.headings" v-bind:key="index" v-bind:class="[heading.class, heading.sortKey === dashboarditem.sort ? sortClassUnsigned : '']" @click="changeSorting(heading.sortKey)">
              {{heading.name}}
              <span id="sorttable_sortrevind" v-if="heading.sortKey === dashboarditem.sort && dashboarditem.direction === 'desc'">&nbsp;</span>
              <span id="sorttable_sortfwdind" v-if="heading.sortKey === dashboarditem.sort && dashboarditem.direction === 'asc'">&nbsp;</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in dashboarditem.items" v-bind:key="item.Kontraktnr">
            <td>
              <a v-if="item.objekt" href="javascript:;" @click="showObjekt(item)" class="primary-title">{{item.objekt.Objektnr}}</a>
              <a v-else href="javascript:;" class="primary-title">-</a>
            </td>
            <td class="nowrap">
              <a v-if="item.kunde" href="javascript:;" @click="showKunde(item)" class="primary-title">{{item.kunde.full_name}}</a>
              <a v-else href="javascript:;" class="primary-title">-</a>
            </td>

            <td class="nowrap" v-tippy :content="`${item.Melding}`">{{item.Melding}}</td>
            <td class="align-right"><span class="opacity-60">{{item.CreatedAt | avantidate}}</span></td>

            <td class="align-right" v-if="item.objekt && item.objekt.Oppgjortransnr">
              <span class="status-container" v-tippy :content="'Sendt til 24 Seven, men feilet under oppdatering av DueDate'">
                <span class="statusicon in-progress"><svg viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-warning"></use></svg></span>
                <span></span>
              </span>
            </td>
            <td class="align-right" v-else-if="item.objekt && !item.objekt.Oppgjortransnr">
              <span class="status-container" v-tippy :content="'Feilet under sending til 24 Seven'">
                <span class="statusicon incomplete"><svg viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-warning"></use></svg></span>
                <span></span>
              </span>
            </td>
            <td class="align-right" v-else>&nbsp;</td>
          </tr>
        </tbody>
        <template v-if="large">
          <tfoot v-if="dashboarditem.pages > 1">
            <tr>
              <td :colspan="dashboarditem.headings.length">
                <div class="pagination-container" style="margin-top: 0; padding: 1rem 0;">
                  <paginate v-model="dashboarditem.page"
                    :page-count="dashboarditem.pages"
                    :page-range="3"
                    :click-handler="setPage"
                    :container-class="'pagination'"
                    :prev-text="paginationArrow"
                    :next-text="paginationArrow"
                    :prev-class="'prev'"
                    :next-class="'next'"
                    >
                  </paginate>
                </div>
              </td>
            </tr>
          </tfoot>
        </template>
      </table>

      <div v-if="dashboarditem.items.length < 1" class="placeholder-container center-container active">
        <div class="center-content">
          <div class="placeholder-icon tiny"><svg width="24px" height="24px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-search"></use></svg></div>
          <h4 class="bold">Fant ingen treff</h4>
          <p class="no-margin">Valgt filter returnerte ingen treff.</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import InvoiceLoggService from '@/services/InvoiceLoggService'
import Vue from 'vue'
import VueTippy from 'vue-tippy'

Vue.use(VueTippy, {
  directive: 'tippy',
  duration: 0,
  delay: 0,
  followCursor: false,
  arrow: true,
  animateFill: false,
  interactive: false,
  allowHTML: true,
  animation: 'fade',
  placement: 'right',
  popperOptions: {
    modifiers: {
      preventOverflow: {
        enabled: true
      }
    }
  }
})

export default {
  name: 'TableFailedOppgjor',
  props: {
    large: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    },
    limit: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      dashboarditem: {
        page: 1,
        pages: 1,
        total: 0,
        items: [],
        loading: true,
        updating: false,
        sort: 'CreatedAt',
        direction: 'desc',
        headings: [
          { name: 'Obj.', sortKey: 'Objekt.Objektnr', class: 'nowrap medium', skelTh: 'nowrap medium sk-text sk-text-narrow', skelTd: 'nowrap medium sk-text sk-text-narrow' },
          { name: 'Kunde', sortKey: 'Kunde.Enavn', class: 'nowrap medium', skelTh: 'nowrap medium sk-text sk-text-narrow', skelTd: 'nowrap medium sk-text sk-text-narrow' },
          { name: 'Melding', sortKey: '', class: 'nowrap unsortable', skelTh: 'nowrap unsortable sk-text sk-text-narrow', skelTd: 'nowrap unsortable sk-text sk-text-narrow' },
          { name: 'Endret', sortKey: 'CreatedAt', class: 'align-right', skelTh: 'align-right sk-text sk-text-narrow', skelTd: 'align-right sk-text sk-text-narrow' },
          { name: 'Info', sortKey: '', class: 'align-right unsortable', skelTh: 'align-right unsortable sk-text sk-text-narrow', skelTd: 'align-right unsortable sk-circle sk-circle-tiny' }
        ]
      }
    }
  },
  filters: {
    toInteger (value) {
      return !value ? 0 : parseInt(value)
    }
  },
  created () {
    this.dashboarditem.loading = true

    this.fetch()
  },
  computed: {
    sortClassUnsigned: function () {
      return 'sorttable_sorted' + (this.dashboarditem.direction !== 'asc' ? '_reverse' : '')
    },
    skeletonThLines: function () {
      return this.dashboarditem.headings.map(o => o.skelTh)
    },
    skeletonTdLines: function () {
      const retval = []

      const tdvals = this.dashboarditem.headings.map(o => o.skelTd)

      for (let i = 0; i < this.limit; ++i) {
        retval.push(tdvals)
      }

      return retval
    }
  },
  methods: {
    setPage: function (page) {
      const vm = this

      window.jQuery('html').animate({
        scrollTop: 0
      }, 200, function () {
        vm.dashboarditem.page = page
        vm.dashboarditem.updating = true
        vm.fetch()
      })
    },
    changeSorting: function (sortKey) {
      if (!sortKey) {
        return
      }

      if (this.dashboarditem.sort === sortKey) {
        this.dashboarditem.direction = this.dashboarditem.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.dashboarditem.direction = 'asc'
      }

      this.dashboarditem.sort = sortKey
      this.dashboarditem.page = 1
      this.dashboarditem.updating = true

      this.fetch()
    },
    fetch: function () {
      let queryParams = {
        page: this.dashboarditem.page,
        sort: this.dashboarditem.sort,
        direction: this.dashboarditem.direction,
        limit: this.limit,
        ansvarlig: this.brukerId
      }

      InvoiceLoggService.getFailedOppgjor(queryParams)
        .then((response) => {
          this.dashboarditem.total = response.headers['x-paging-count'] ? parseInt(response.headers['x-paging-count']) : 0
          this.dashboarditem.pages = response.headers['x-paging-pagecount'] ? parseInt(response.headers['x-paging-pagecount']) : 1
          this.dashboarditem.items = response.data
        })
        .catch((error) => {
          this.$helpers.showErrorAlert('Feil', 'Kunne ikke laste inn oppgjør')
          console.log(error)
        })
        .then(() => {
          this.dashboarditem.loading = false
          this.dashboarditem.updating = false

          this.$emit('tableLoaded', this.dashboarditem)
        })
    },
    showKunde: function (item) {
      this.$router.push({
        name: 'KundeCard',
        params: { kunde_id: item.kunde.Kundenr },
        hash: ''
      })
    },
    showObjekt: function (item) {
      sessionStorage.showObjektTab = 'selger'

      this.loadObjektModal(item.objekt.Objektid)

      // this.$router.push({
      //   name: 'KundeCard',
      //   params: { kunde_id: item.kunde.Kundenr },
      //   query: { kontrakt: item.objekt.Kontraktnr, objekt: item.objekt.Objektid },
      //   hash: ''
      // })
    }
  }
}
</script>
<style scoped>
  a {
    text-decoration: underline;
  }
  table.fixed.oddeven tbody tr:nth-child(odd) td:before {
    background-color: rgba(0, 0, 0, 0.015);
    display: none;
  }
  table.fixed.oddeven tbody tr:nth-child(odd) td {
    background-color: rgba(0, 0, 0, 0.015);
  }
</style>



// WEBPACK FOOTER //
// src/components/Admin/Dashboard/TableFailedOppgjor.vue